/**
 * Cause Report Utilities
 */

import type { FilterConfig } from '../../../records/filter';
import type { ConductDimensionId, HighRiskRecord } from '../types';

/** Shared locale for date formatting across the cause report */
export const CAUSE_REPORT_LOCALE = 'en-GB';

/** Valid conduct dimension IDs */
const VALID_DIMENSIONS = new Set<string>(['foreseeable_harm', 'customer_strain', 'dissatisfaction']);

/**
 * Validate and filter dimension strings to known ConductDimensionId values.
 * Prevents runtime crashes from unexpected API data.
 */
export function validateDimensions(dims: string[]): ConductDimensionId[] {
  return dims.filter((d): d is ConductDimensionId => VALID_DIMENSIONS.has(d));
}

/** API record shape (snake_case) shared across hooks */
export interface ApiRecord {
  id: string;
  conduct_score: number;
  summary: string;
  customer_name: string;
  date: string;
  journey_stage?: string;
  channel?: string;
  product?: string;
  root_cause_labels: Array<{ id: string; name: string }>;
  dimensions: string[];
}

/**
 * Transform a snake_case API record to the camelCase HighRiskRecord type.
 * Validates dimensions to prevent crashes from unexpected values.
 */
export function transformRecord(rec: ApiRecord): HighRiskRecord {
  return {
    id: rec.id,
    conductScore: rec.conduct_score,
    summary: rec.summary,
    customerName: rec.customer_name,
    date: rec.date,
    journeyStage: rec.journey_stage,
    channel: rec.channel,
    product: rec.product,
    rootCauseLabels: rec.root_cause_labels,
    dimensions: validateDimensions(rec.dimensions),
  };
}

/**
 * Map FilterConfig to API query parameters (snake_case).
 * Converts frontend filter state to the format expected by the API.
 */
export function mapFiltersToApiParams(
  filters: FilterConfig
): Record<string, string> {
  const params: Record<string, string> = {};

  // Date range
  if (filters.dateRange?.preset) {
    params.date_range_preset = filters.dateRange.preset;
  }
  if (filters.dateRange?.from) {
    params.custom_date_from = filters.dateRange.from;
  }
  if (filters.dateRange?.to) {
    params.custom_date_to = filters.dateRange.to;
  }

  // Business context filters
  if (filters.businessContext?.productCategories?.values?.length) {
    params.product_categories = filters.businessContext.productCategories.values.join(',');
    if (filters.businessContext.productCategories.logic) {
      params.product_categories_logic = filters.businessContext.productCategories.logic;
    }
  }
  if (filters.businessContext?.channels?.values?.length) {
    params.channels = filters.businessContext.channels.values.join(',');
    if (filters.businessContext.channels.logic) {
      params.channels_logic = filters.businessContext.channels.logic;
    }
  }
  if (filters.businessContext?.journeyStages?.values?.length) {
    params.journey_stages = filters.businessContext.journeyStages.values.join(',');
    if (filters.businessContext.journeyStages.logic) {
      params.journey_stages_logic = filters.businessContext.journeyStages.logic;
    }
  }

  // Dimensions
  if (filters.dimensions?.values?.length) {
    params.dimensions = filters.dimensions.values.join(',');
    if (filters.dimensions.logic) {
      params.dimensions_logic = filters.dimensions.logic;
    }
  }

  // Regulatory flags
  if (filters.regulatoryFlags?.values?.length) {
    params.regulatory_flags = filters.regulatoryFlags.values.join(',');
    if (filters.regulatoryFlags.logic) {
      params.regulatory_flags_logic = filters.regulatoryFlags.logic;
    }
  }

  // Search
  if (filters.search) {
    params.search = filters.search;
  }

  // Conduct score range
  if (filters.conductScoreMin !== undefined) {
    params.conduct_score_min = String(filters.conductScoreMin);
  }
  if (filters.conductScoreMax !== undefined) {
    params.conduct_score_max = String(filters.conductScoreMax);
  }

  return params;
}
